import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';

import { formatAssayDetailsDate, formatAssayDetailsDatetime } from '../../../utils/date_utils';

import Button from '../../common/Button/Button';
import CardView from '../../common/CardView/CardView';
import Icon from '../../common/icons/Icon';
import Label from '../../common/Labels/Label';
import ModalOverlay from '../../common/ModalOverlay/ModalOverlay';
import ModelIcon from '../../common/icons/ModelIcon';
import { calculateNextRun } from '../../../utils/assay_utils';
import { getThemeColor } from '../../../utils/theme-utils';

export const AssayDetailAttribute = ({
  label,
  children,
  icon = null,
  useTailwindSizing = false,
}) => (
  <div className="flex flex-col items-start">
    <Label bold>{label}</Label>
    <div className="flex justify-center items-center">
      {
        icon && (
          <Icon className="mr-2" fill={getThemeColor('brand')} icon={icon} useTailwindSizing={useTailwindSizing} size={24} />
        )
      }
      <Label>{children}</Label>
    </div>
  </div>
)

const getBaselineRangeString = (baselineStart, baselineEnd) => `${formatAssayDetailsDate(baselineStart)} >> ${formatAssayDetailsDate(baselineEnd)}`

const areBinsEquallyWeighted = (binWeights = []) => binWeights.every(value => value === binWeights[0]);

export const AssayDetailFrame = ({ assayDetails }) => {
  const baselineStart = assayDetails?.baselineStart;
  const baselineEnd = assayDetails?.baselineEnd;

  const lastRun = useMemo(() => {
    if (!assayDetails.lastRun) {
      return '';
    }
    return formatAssayDetailsDatetime(assayDetails.lastRun);
  }, [assayDetails])

  const nextRun = useMemo(() => {
    const calcNextRun = calculateNextRun(assayDetails);
    return formatAssayDetailsDatetime(calcNextRun.toISOString());
  }, [assayDetails])

  const splitIopath = assayDetails?.iopath?.split(' ')
  const monitoryType = splitIopath?.[0]
  const monitoryTypeCapitalized = monitoryType ? `${monitoryType.charAt(0).toUpperCase()}${monitoryType.slice(1)}` : 'Monitor';
  const namedField = splitIopath?.[1];
  const index = splitIopath?.[2];
  const threshold = assayDetails?.alertThreshold;
  const aggType = assayDetails?.summarizerMetadata?.aggregation;
  const binMode = assayDetails?.summarizerMetadata?.binMode;
  const metric = assayDetails?.summarizerMetadata?.metric;
  const numBins = assayDetails?.summarizerMetadata?.numBins;
  const binWeights = assayDetails?.summarizerMetadata?.binWeights;

  const binWeightString = (!binWeights || areBinsEquallyWeighted(binWeights)) ? 'Equally Weighted' : 'Provided';

  const baselineStr = useMemo(() => {
    if (!baselineStart || !baselineEnd) {
      return '';
    }
    return getBaselineRangeString(baselineStart, baselineEnd)
  }, [baselineStart, baselineEnd]);

  return (
    <div className="flex flex-col">
      <div className="grid grid-cols-3 gap-4 mt-4">
        <AssayDetailAttribute label={monitoryTypeCapitalized}>{namedField}</AssayDetailAttribute>
        <AssayDetailAttribute label="Index">{index}</AssayDetailAttribute>
      </div>
      <div className="grid grid-cols-3 gap-4 mt-4">
        <AssayDetailAttribute label="Baseline">{baselineStr}</AssayDetailAttribute>
        <AssayDetailAttribute label="Last Run">{lastRun}</AssayDetailAttribute>
        <AssayDetailAttribute label="Next Run">{nextRun}</AssayDetailAttribute>
        <AssayDetailAttribute label="Aggregation Type">{aggType}</AssayDetailAttribute>
        <AssayDetailAttribute label="Threshold">{threshold}</AssayDetailAttribute>
        <AssayDetailAttribute label="Metric">{metric}</AssayDetailAttribute>
        <AssayDetailAttribute label="Number of Bins">{numBins}</AssayDetailAttribute>
        <AssayDetailAttribute label="Bin Weight">{binWeightString}</AssayDetailAttribute>
        <AssayDetailAttribute label="Bin Mode">{binMode}</AssayDetailAttribute>
      </div>
    </div>
  )
}

export const AssayDetailModal = ({
  assayDetails,
  onModalToggle,
  isShown = false,
}) => {
  const [show, setShow] = useState(isShown);

  const assayName = assayDetails?.name;

  const closeModal = useCallback(() => {
    setShow(false);
    onModalToggle(false)
  }, [onModalToggle])

  useEffect(() => {
    setShow(isShown);
  }, [isShown])

  if (!show) {
    return null;
  }

  return (
    <ModalOverlay active={show} onModalToggle={closeModal}>
      <div className="relative">
        <CardView className="flex flex-col justify-evenly relative" disableFullHeight>
          <div className="flex justify-between items-center mb-2">
            <Label size="lg" bold>{assayName}</Label>
            <Button
              variant="close"
              textColor="border-dark"
              hoverTextColor="border-darker"
              activeTextColor="info"
              onClick={closeModal}
            />
          </div>
          <AssayDetailFrame assayDetails={assayDetails} />
        </CardView>
      </div>
    </ModalOverlay>
  )
}

export default AssayDetailModal;
